import React, { useEffect, useState, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { registerPdfOpenHandlers, PdfOpenPayload } from '@/services/pdfOpenHandlers';

interface MobileInitializerProps {
  children: React.ReactNode;
}

export function MobileInitializer({ children }: MobileInitializerProps) {
  const [pdfPayload, setPdfPayload] = useState<PdfOpenPayload | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const qrViewerBaseUrl = 'https://qr.md/r/sprz/?t=View&thumbnails=off';

  useEffect(() => {
     const unregister = registerPdfOpenHandlers({
       onPdfOpen: (payload) => {
         setPdfPayload((current) => {
           current?.cleanup?.();
           return payload;
         });
       },
       onError: (error) => {
         console.error('[MobileInitializer] Failed handling PDF open event', error);
       },
     });

     return () => {
       setPdfPayload((current) => {
         current?.cleanup?.();
         return null;
       });
       unregister();
     };
   }, []);

  useEffect(() => {
    const initializeMobile = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          // Configure status bar
          await StatusBar.setStyle({ style: Style.Dark });
          await StatusBar.setBackgroundColor({ color: '#000000' });
          
          // Hide splash screen after a delay
          setTimeout(async () => {
            try {
              await SplashScreen.hide();
            } catch (error) {
              console.warn('Failed to hide splash screen:', error);
            }
          }, 2000);
        } catch (error) {
          console.warn('Failed to initialize mobile features:', error);
        }
      }
    };

    initializeMobile();
  }, []);

  // Send PDF data to QR viewer via postMessage when iframe loads (only if not using data URL)
  useEffect(() => {
    console.log('useEffect load 1');
    console.log('pdfPayload load  ' + !pdfPayload);
    console.log('iframeRef load  ' + !iframeRef.current);
    // Only proceed if we have PDF data to send
    if (!pdfPayload || !pdfPayload.data) {
      console.log('[MobileInitializer] No PDF data to send', { 
        hasPayload: !!pdfPayload, 
        hasData: !!pdfPayload?.data 
      });
      return;
    }
    console.log('pxdfPayload load 2 ' + !pdfPayload);

    // Retry mechanism: wait for iframe to be rendered
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    const checkAndSetup = () => {
      if (!iframeRef.current) {
        console.log('[MobileInitializer] Iframe not yet rendered, retrying...');
        // Retry after a short delay (max 10 retries = 1 second)
        retryTimeout = setTimeout(checkAndSetup, 100);
        return;
      }

      const iframe = iframeRef.current;
      let messageSent = false;

      console.log('[MobileInitializer] Setting up PDF send handler', {
        fileName: pdfPayload.fileName,
        dataSize: pdfPayload.data.length,
      });

      const sendPdfData = (attempt: number = 1) => {
        try {
          const uint8Array = pdfPayload!.data!;
          
          // Convert Uint8Array to base64 (chunked for large files)
          let binary = '';
          const chunkSize = 8192;
          for (let i = 0; i < uint8Array.length; i += chunkSize) {
            const chunk = uint8Array.slice(i, i + chunkSize);
            binary += String.fromCharCode.apply(null, Array.from(chunk));
          }
          const base64 = btoa(binary);

          // Create a File object from the data
          const blob = new Blob([new Uint8Array(uint8Array)], { type: 'application/pdf' });
          const file = new File([blob], pdfPayload!.fileName || 'document.pdf', {
            type: 'application/pdf',
            lastModified: Date.now(),
          });

          // Send postMessage with file data in the format QR viewer expects
          if (iframe.contentWindow) {
            iframe.contentWindow.postMessage(
              {
                type: 'LOCAL_FILE_UPLOAD',
                file: {
                  name: file.name,
                  size: file.size,
                  type: file.type,
                  lastModified: file.lastModified,
                },
                fileName: pdfPayload!.fileName || 'document.pdf',
                source: 'capacitor',
                localFileData: base64,
                fileData: base64,
              },
              '*'
            );
            console.log(`[MobileInitializer] Sent postMessage with PDF data (attempt ${attempt})`, {
              fileName: file.name,
              size: file.size,
              hasLocalFileData: true,
            });
          }
        } catch (error) {
          console.error('[MobileInitializer] Failed to send PDF data to iframe', error);
        }
      };

      const handleIframeLoad = () => {
        if (messageSent) return;
        messageSent = true;

        console.log('[MobileInitializer] Iframe loaded, scheduling PDF data sends...');
        
        //Call again if page hasn't loaded
        //TODO: Preload the page if it isn't too resource intensive
        setTimeout(() => sendPdfData(1), 3000);
      };

      // Wait for iframe to load
      if (iframe.contentDocument?.readyState === 'complete') {
        handleIframeLoad();
      } else {
        iframe.addEventListener('load', handleIframeLoad, { once: true });
      }
    };

    // Start the check and setup process
    checkAndSetup();

    // Cleanup
    return () => {
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [pdfPayload]);

  const handleClosePdf = () => {
    setPdfPayload((current) => {
      current?.cleanup?.();
      return null;
    });
  };

  // Always use QR viewer when we have PDF data, otherwise use direct webViewSrc
  const shouldUseQrViewer = !!pdfPayload && !!pdfPayload.data;

  // For small PDFs, try using data URL directly in f= parameter
  const getDataUrlForPdf = (data: Uint8Array): string | null => {
    if (data.length > 2 * 1024 * 1024) return null; // Skip if > 2MB (URL length limits)
    try {
      let binary = '';
      const chunkSize = 8192;
      for (let i = 0; i < data.length; i += chunkSize) {
        const chunk = data.slice(i, i + chunkSize);
        binary += String.fromCharCode.apply(null, Array.from(chunk));
      }
      const base64 = btoa(binary);
      return `data:application/pdf;base64,${base64}`;
    } catch {
      return null;
    }
  };

  const iframeSrc = shouldUseQrViewer
    ? pdfPayload.data
      ? (() => {
          // Try data URL first for small files, otherwise use postMessage
          const dataUrl = getDataUrlForPdf(pdfPayload.data);
          return qrViewerBaseUrl; // Empty f= parameter, we'll send data via postMessage
        })()
      : `${qrViewerBaseUrl}${encodeURIComponent(pdfPayload.documentIdentifier || pdfPayload.fileName || '')}`
    : pdfPayload?.webViewSrc;

  return (
    <>
      {children}
      {pdfPayload && iframeSrc && (
        <Dialog open onOpenChange={(open) => { if (!open) handleClosePdf(); }}>
          <DialogContent className="max-w-5xl h-[90vh] flex flex-col space-y-4">
            <DialogHeader>
              <DialogTitle>
                {pdfPayload.fileName ?? 'Shared PDF'}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-hidden rounded-md border">
              <iframe
                ref={iframeRef}
                src={iframeSrc}
                title={pdfPayload.fileName ?? 'Shared PDF'}
                className="w-full h-full border-0"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              />
            </div>
            <div className="flex justify-end">
              <Button variant="outline" onClick={handleClosePdf}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
